import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Users, X } from 'lucide-react';
import { onAuthStateChanged } from 'firebase/auth';
import { safeFetch } from '../lib/api';
import { auth } from '../lib/firebase';

/**
 * In-app prompt for pending WORKSPACE TEAM invitations addressed to the
 * signed-in user in their CURRENT workspace context.
 *
 * Owner rule D-8: workspaces own their users and the URL determines the
 * context — an invitation only ever surfaces in the workspace it was issued
 * for (the server scopes GET /team-invitations/mine to the caller's resolved
 * workspace + own email), and accepting binds the caller into THAT workspace.
 * Nothing here navigates the user into another workspace.
 */
export const WorkspaceTeamInvitePrompt: React.FC = () => {
  const [invitations, setInvitations] = useState<any[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [acceptedName, setAcceptedName] = useState<string>('');

  const loadMine = useCallback(async () => {
    try {
      const data = await safeFetch('/api/workspaces/team-invitations/mine');
      setInvitations(Array.isArray(data?.invitations) ? data.invitations : []);
    } catch {
      // Silent: no prompt is better than a broken one; the invite also
      // exists in the invitee's email if this check ever fails.
      setInvitations([]);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.pathname === '/accept-invitation') return;
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) loadMine();
      else setInvitations([]);
    });
    return unsub;
  }, [loadMine]);

  const accept = async (inv: any) => {
    setBusyId(inv.id);
    setError('');
    try {
      await safeFetch(`/api/workspaces/team-invitations/${inv.id}/accept-mine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      setAcceptedName(inv.workspaceName || 'the workspace');
      setInvitations((prev) => prev.filter((i) => i.id !== inv.id));
      // Reload into the same workspace context so the shell re-resolves
      // membership, navigation and permissions with the new team role.
      setTimeout(() => window.location.assign('/dashboard'), 900);
    } catch (e: any) {
      setError((e?.message || 'Failed to accept the invitation.').toString());
    } finally {
      setBusyId(null);
    }
  };

  const decline = async (inv: any) => {
    setBusyId(inv.id);
    setError('');
    try {
      await safeFetch(`/api/workspaces/team-invitations/${inv.id}/decline-mine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      setInvitations((prev) => prev.filter((i) => i.id !== inv.id));
    } catch (e: any) {
      setError((e?.message || 'Failed to decline the invitation.').toString());
    } finally {
      setBusyId(null);
    }
  };

  if (acceptedName) {
    return (
      <div className="fixed bottom-4 right-4 z-[90] w-[calc(100vw-2rem)] max-w-sm rounded-2xl border border-emerald-100 bg-white shadow-2xl p-4">
        <p className="text-sm font-bold text-slate-900">You're on the workspace team 🎉</p>
        <p className="text-xs text-slate-500 mt-1">You joined <strong>{acceptedName}</strong>. Loading your dashboard…</p>
      </div>
    );
  }

  if (invitations.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[90] w-[calc(100vw-2rem)] max-w-sm space-y-2">
      {error && (
        <div role="alert" className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-xs text-rose-700 shadow-lg">
          {error}
        </div>
      )}
      {invitations.map((inv) => (
        <div key={inv.id} className="rounded-2xl border border-slate-200 bg-white shadow-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
              <Users className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-slate-900">Workspace team invitation</p>
              <p className="text-xs text-slate-500 mt-0.5">
                You've been invited to join <strong className="text-slate-700">{inv.workspaceName || 'this workspace'}</strong> as a{' '}
                <strong className="text-slate-700">{inv.memberRoleLabel || inv.memberRole || 'Team Member'}</strong>.
              </p>
            </div>
            <button
              onClick={() => setInvitations((prev) => prev.filter((i) => i.id !== inv.id))}
              disabled={busyId === inv.id}
              className="p-1 rounded-lg text-slate-300 hover:text-slate-500 hover:bg-slate-50 cursor-pointer disabled:opacity-50"
              aria-label="Dismiss invitation for now"
              title="Dismiss for now (the invitation stays pending)"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => accept(inv)}
              disabled={busyId === inv.id}
              className="flex-1 px-3 py-2 rounded-xl bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 cursor-pointer disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {busyId === inv.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Accept invitation
            </button>
            <button
              onClick={() => decline(inv)}
              disabled={busyId === inv.id}
              className="px-3 py-2 rounded-xl text-xs font-bold text-slate-600 bg-slate-50 border border-slate-200 hover:bg-slate-100 cursor-pointer disabled:opacity-50"
            >
              Decline
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default WorkspaceTeamInvitePrompt;
